import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useUserManagementLocations } from '../hooks/useUsers';
import { useUserManagement } from '../hooks/useUserManagement';
import type { User, UserRole } from '../types';
import { userRoleSchema } from '@threadheads/shared';
import ViewerPagePicker from './ViewerPagePicker';
import { DEFAULT_VIEWER_PAGES } from '@/lib/page-registry';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { DTF_FLOOR_HINT, dtfFloorPick, pickerRoles } from '../utils/assignable-roles';

interface EditUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
  /**
   * Forces client mode when true; omitted follows the server's pure-client verdict.
   * The onboarding invite step passes true for a client org.
   */
  clientMode?: boolean;
}

export default function EditUserModal({ open, onOpenChange, user, clientMode }: EditUserModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>('Warehouse Staff');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [viewerVisiblePages, setViewerVisiblePages] = useState<string[]>([]);
  const { profile: _profile } = useAuth();

  // Use hooks for data and operations
  const { locations } = useUserManagementLocations();
  const { updateUser } = useUserManagement();

  // Client mode: a pure-client 3PL org has one login type, so there is nothing to
  // edit about role or location access. Without this, a client admin could demote
  // someone to Picker (defeating the single login type) and the location-required
  // submit gate below would make every location-less client Admin un-editable.
  const { isPureClient, hidesProduction } = useOrgRoleSurfaces();
  const isClientMode = clientMode ?? isPureClient;
  const { organization } = useOrganization();
  const roleOptions = pickerRoles({ runsOwnFloor: !hidesProduction });
  const locationsRequired = !isClientMode;

  // A DTF Printer is offered, and submits, only floors the org owns; the submit gate
  // counts those, not every box ticked before the role was switched.
  const { floors, submittedIds: submittedLocations, isDtf } = dtfFloorPick(
    locations ?? [],
    role,
    organization?.id,
    selectedLocations,
  );
  const needsFloor = isDtf ? submittedLocations.length === 0 : locationsRequired && selectedLocations.length === 0;

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name);
      setLastName(user.last_name);
      // Validate against the shared enum, never a hand-typed chain — a chain
      // here omitted 'CX', so the modal showed (and on save, WROTE) the
      // 'Warehouse Staff' default for genuinely-CX users.
      const parsedRole = userRoleSchema.safeParse(user.role);
      if (parsedRole.success) {
        setRole(parsedRole.data);
      }
      // Safely map locations with null checks
      if (user.profile_locations) {
        setSelectedLocations(
          user.profile_locations
            .filter(pl => pl.location !== null)
            .map(pl => pl.location!.id)
        );
      }
      setViewerVisiblePages(user.viewer_visible_pages ?? []);
    }
  }, [user]);

  // When the admin flips an existing non-Viewer to Viewer, seed the picker
  // with the default page list so something reasonable persists if they save
  // without touching the picker.
  useEffect(() => {
    if (role === 'Viewer' && viewerVisiblePages.length === 0) {
      setViewerVisiblePages([...DEFAULT_VIEWER_PAGES]);
    }
  }, [role, viewerVisiblePages.length]);


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateUser.mutate(
      {
        userId: user.id,
        firstName,
        lastName,
        // Submit the role unchanged in client mode — the picker is hidden, so
        // there is no intent to change it.
        ...(isClientMode ? {} : { role }),
        ...(isClientMode ? {} : { locationIds: submittedLocations }),
        ...(!isClientMode && role === 'Viewer' ? { viewerVisiblePages } : {}),
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  const toggleLocation = (locationId: string) => {
    setSelectedLocations(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px] max-h-[90dvh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              {isClientMode
                ? 'Update this user\u2019s name. Everyone in your organization has the same access.'
                : 'Update user information and location access.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={user.email}
                disabled
                className="bg-muted"
              />
            </div>
            {!isClientMode && (
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Developer role, granted out of band; shown only so an existing
                      Super Admin row renders its own value instead of a blank select. */}
                  {user.role === 'Super Admin' && (
                    <SelectItem value="Super Admin">Super Admin</SelectItem>
                  )}
                  {roleOptions.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                  {/* An existing DTF Printer in an org that no longer runs a floor of its
                      own: the picker drops the role, so keep the item to avoid a blank select. */}
                  {user.role === 'DTF Printer' && !roleOptions.includes('DTF Printer') && (
                    <SelectItem value="DTF Printer">DTF Printer</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            )}
            {!isClientMode && (
            <div className="space-y-2">
              <Label>Location Access</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-3">
                {floors.map((location) => (
                  <div key={location.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={location.id}
                      checked={selectedLocations.includes(location.id)}
                      onCheckedChange={() => toggleLocation(location.id)}
                    />
                    <Label
                      htmlFor={location.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {location.name}
                    </Label>
                  </div>
                ))}
              </div>
              {isDtf && (
                <p className="text-xs text-muted-foreground">
                  {DTF_FLOOR_HINT}
                </p>
              )}
              {needsFloor && (
                <p className="text-xs text-destructive">
                  Select at least one location. A user with no location access can’t sign in.
                </p>
              )}
            </div>
            )}
            {!isClientMode && role === 'Viewer' && (
              <ViewerPagePicker value={viewerVisiblePages} onChange={setViewerVisiblePages} />
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateUser.isPending || needsFloor}
            >
              {updateUser.isPending ? 'Updating...' : 'Update User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
